import type { ReactNode } from "react";

import { cn } from "@/shared/lib/cn";

type TooltipSide = "top" | "bottom" | "end";
type TooltipAlign = "center" | "start" | "end";

const SIDE: Record<TooltipSide, string> = {
  top: "bottom-full mb-1.5",
  bottom: "top-full mt-1.5",
  /* Beside the control, toward the page: for a rail of icons along the start edge. */
  end: "start-full top-1/2 ms-2.5 -translate-y-1/2",
};

const ALIGN: Record<TooltipAlign, string> = {
  center: "start-1/2 -translate-x-1/2 rtl:translate-x-1/2",
  start: "start-0",
  end: "end-0",
};

interface TooltipProps {
  /** The same words as the control's accessible name (its aria-label). */
  label: string;
  side?: TooltipSide;
  /** Line the tip up with the control's start or end edge near the edge of a panel. */
  align?: TooltipAlign;
  children: ReactNode;
  className?: string;
}

/**
 * A short label over an icon-only control, on hover and on keyboard focus. Screen readers already
 * hear the control's aria-label, so the tip itself is hidden from them. It waits a moment on hover
 * so it never flickers while the pointer passes over.
 */
export function Tooltip({
  label,
  side = "top",
  align = "center",
  children,
  className,
}: TooltipProps) {
  return (
    <span className={cn("group/tooltip relative inline-flex", className)}>
      {children}
      <span
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute z-50 rounded-md bg-ink px-2 py-1 text-[12.5px] font-medium whitespace-nowrap text-ink-inverse opacity-0 shadow-md transition-opacity duration-150",
          "group-hover/tooltip:opacity-100 group-hover/tooltip:delay-500 group-has-[:focus-visible]/tooltip:opacity-100",
          SIDE[side],
          side === "end" ? null : ALIGN[align],
        )}
      >
        {label}
      </span>
    </span>
  );
}
